import { useEffect, useState } from 'react';
import './MinisterPageDetail.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import MainURL from '../../../../MainURL';
import youtubePlay from '../../../../images/Youtube_logo.png';
import Loading from '../../../../components/Loading';
import {
  buildTimeline,
  copyText,
  profileImageFileSrc,
  profileMainImageSrc,
  profilePhotoSrc,
  profileShareUrl,
  profileSubTitle,
} from './ministerPageUtils';
import type { MinisterProfile } from './ministerPageUtils';

// 프로필페이지 상세 — 공통 데이터(이력서와 함께 관리) + 프로필페이지 전용 데이터
// ?id= 프로필페이지 id, ?legacyId= 예전 /ministerpage 주소의 ministerMain id
export default function MinisterPageDetail () {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ID = searchParams.get('id') || '';
  const legacyId = searchParams.get('legacyId') || '';

  const [profileData, setProfileData] = useState<MinisterProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    axios.get(`${MainURL}/ministerprofile/detail/${ID || '0'}`, { params: legacyId ? { legacyId } : undefined })
      .then((res) => {
        if (cancelled) return;
        setProfileData(res.data?.success ? res.data.profile : null);
      })
      .catch((error) => {
        if (cancelled) return;
        setProfileData(null);
        setErrorText(error?.response?.data?.error || '');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ID, legacyId]);

  const goList = () => {
    navigate('/minister/profilepage');
    window.scrollTo(0, 0);
  };

  const copyLink = async () => {
    if (!profileData) return;
    await copyText(profileShareUrl(profileData.id));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (isLoading) {
    return (
      <div className="ministerDetail ministerDetail--state">
        <Loading />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="ministerDetail ministerDetail--state">
        <p>{errorText || '프로필페이지를 찾을 수 없습니다.'}</p>
        <button type="button" className="ministerDetail__back" onClick={goList}>목록으로</button>
      </div>
    );
  }

  const mainImageSrc = profileMainImageSrc(profileData);
  const photoSrc = profilePhotoSrc(profileData);
  const timeline = buildTimeline(profileData);
  const gallery = profileData.portfolioItems.filter((item) => item.category === '사역사진' && item.image);
  const videos = profileData.portfolioItems.filter((item) => item.category !== '사역사진' && item.link);
  const hasContact = Boolean(profileData.phone || profileData.email);

  return (
    <div className="ministerDetail">

      <div className="inner">
        <div className="subpage__main" style={{ width: '100%' }}>
          {/* HEADER / HERO */}
          <div className="homapage_main">
            <div className="homapage_main_imagebox">
              {mainImageSrc && <img src={mainImageSrc} alt="mainImage" />}
              <div className="homapage_main_title">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0 }}>
                  <p className="homapage_main_title-name">{profileData.name}</p>
                </motion.div>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }}>
                  <p className="homapage_main_title-sort">{profileSubTitle(profileData)}</p>
                </motion.div>
              </div>
              {
                profileData.isOwner && (
                  <div className="edit_button_container">
                    <button
                      className="edit_button"
                      onClick={() => navigate('/mypage/profilepage')}
                    >
                      수정하기
                    </button>
                  </div>
                )
              }
            </div>


            <div className="after_hero">
              {profileData.isOwner && !profileData.isPublic && (
                <p className="ministerDetail__private">비공개 상태입니다. 본인에게만 보이는 미리보기예요.</p>
              )}
              <div className="personalpage_detail_bottomRow"></div>

            {/* CORE INFO CARD */}
            <div className="personalpage_detail_titlebox">
              <p className="personalpage_detail_title">INFO</p>
            </div>
            <div className="profile_card">
              <div className="profile_card_left">
                {photoSrc && <img className="profile_avatar" src={photoSrc} alt="profile" />}
              </div>
              <div className="profile_card_right">
                <div className="profile_row">
                  <span className="profile_key">이름</span>
                  <span className="profile_value">{profileData.name} {profileData.dutyTitle}</span>
                </div>
                <div className="profile_row">
                  <span className="profile_key">구분</span>
                  <span className="profile_value">{profileData.sort}</span>
                </div>
                <div className="profile_row">
                  <span className="profile_key">교단</span>
                  <span className="profile_value">{profileData.religiousbody}</span>
                </div>
                <div className="profile_row">
                  <span className="profile_key">교회</span>
                  <span className="profile_value">{profileData.church}</span>
                </div>
                <div className="profile_row">
                  <span className="profile_key">담당 사역</span>
                  <span className="profile_value">{profileData.ministryFields.join(', ')}</span>
                </div>
              </div>
            </div>

            <div className="personalpage_detail_bottomRow"></div>

            {/* JOURNEY TIMELINE */}
            {
              timeline.length > 0 && (
                <>
                <div className="personalpage_detail_titlebox">
                  <p className="personalpage_detail_title">PROFILE</p>
                </div>
                <div className="journey">
                  {timeline.map((item, index) => (
                    <div className="journey_item" key={index}>
                      <div className="journey_year">{item.year}</div>
                      <div className="journey_content">
                        {item.content.map((content, subindex) => (
                          <div className="journey_text_indented" key={subindex}>{content}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="personalpage_detail_bottomRow"></div>
                </>
              )
            }

            {/* IMAGE SECTION */}
            {
              gallery.length > 0 && (
                <>
                <div className="personalpage_detail_titlebox">
                  <p className="personalpage_detail_title">GALLERY</p>
                </div>
                <div className="personalpage-imagebox" style={{ flexDirection: 'column', gap: 20 }}>
                  {gallery.map((item, idx) => (
                    <img key={idx} src={profileImageFileSrc(item.image)} alt={item.title || `personal-${idx}`} />
                  ))}
                </div>

                <div className="personalpage_detail_bottomRow"></div>
                </>
              )
            }

            {/* YOUTUBE VIDEOS SECTION */}
            {
              videos.length > 0 && (
                <>
                <div className="personalpage_detail_titlebox">
                  <p className="personalpage_detail_title">VIDEOS</p>
                </div>
                <div className="youtube_section">
                  {videos.map((item, index) => (
                    <div key={index}
                    className="youtube_imagebox"
                    onClick={() => window.open(item.link, '_blank')}>
                      <img src={item.image ? profileImageFileSrc(item.image) : '/placeholder-video.jpg'} alt={item.title || 'youtube'} className="youtube_image_thumbnail"/>
                      <div  className="youtube_image_playbtn_cover">
                        <img src={youtubePlay} alt="play" className="youtube_image_playbtn_image" />
                      </div>
                    </div>
                  ))}
                </div>


                <div className="personalpage_detail_bottomRow"></div>
                </>
              )
            }


            {/* CONTACT SECTION — 본인이 공개를 고른 연락처만 */}
            {
              hasContact && (
                <>
                <div className="personalpage_detail_titlebox">
                  <p className="personalpage_detail_title">CONTACT</p>
                </div>
                <div className="personalpage-contactbox">
                  <div className="personalpage-contact-content">
                    <p className="personalpage-contact-part">{profileData.dutyTitle}</p>
                    <p className="personalpage-contact-nameEn">{profileData.name}</p>

                    {
                      profileData.phone && (
                        <p className="personalpage-contact-phone">
                          Phone: <a href={`tel:${profileData.phone}`}>{profileData.phone}</a>
                        </p>
                      )
                    }
                    {
                      profileData.email && (
                        <p className="personalpage-contact-email">
                          Email: {profileData.email}
                        </p>
                      )
                    }
                  </div>
                </div>
                </>
              )
            }

            <div className="ministerDetail__footer">
              <button type="button" className="ministerDetail__back" onClick={goList}>목록으로</button>
              <button type="button" className="ministerDetail__back ministerDetail__link" onClick={copyLink}>
                {copied ? '링크가 복사되었습니다' : '링크 복사'}
              </button>
            </div>

            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
